import React, { useCallback, useMemo, useState } from "react";
import {
  Row,
  Col,
  Card,
  Button,
  Icons,
  Input,
  Space,
  Typography,
} from "@pankod/refine-antd";
import { useCustom } from "@pankod/refine-core";
import dayjs from "dayjs";
import ENV from "utils/env";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { KpiCard } from "../charts/KpiCards";
import { PrizmaScreensTable } from "../tables/PrizmaScreensTable";
import { extractArray } from "../utils";
import {
  PrizmaScreen,
  GAP_KIND_LABELS,
  OPEN_HOURS_LABEL,
  STATUS_LABELS,
  formatDateTime,
  formatVideo,
  shopLabel,
} from "../prizma";

const matchesSearch = (screen: PrizmaScreen, query: string) =>
  [
    screen.shopCode,
    screen.shopName,
    screen.shop?.name,
    screen.shop?.city,
    screen.screen,
    screen.screenId ? String(screen.screenId) : null,
    screen.deviceUuid,
  ].some((value) => value && value.toLocaleLowerCase("tr-TR").includes(query));

// Canlı durum: tarih aralığı ve kiosk mağaza filtresi bu sekmeye uygulanmaz.
export const PrizmaScreensTab: React.FC = () => {
  const [search, setSearch] = useState("");

  const {
    data: response,
    isLoading,
    isFetching,
    dataUpdatedAt,
    refetch,
  } = useCustom({
    url: `${ENV.API_URL}/api/analytics/prizma/screens`,
    method: "get",
    queryOptions: { retry: 0, refetchInterval: 60000, keepPreviousData: true },
  });

  const allScreens = useMemo(
    () => extractArray<PrizmaScreen>(response?.data),
    [response]
  );

  const screens = useMemo(() => {
    const query = search.trim().toLocaleLowerCase("tr-TR");
    return query
      ? allScreens.filter((screen) => matchesSearch(screen, query))
      : allScreens;
  }, [allScreens, search]);

  const summary = useMemo(
    () => ({
      total: screens.length,
      online: screens.filter((s) => s.status === "online").length,
      noSignal: screens.filter(
        (s) => s.status === "silent" || s.status === "notOpened"
      ).length,
      shopUnmatched: screens.filter((s) => !s.shopMatched).length,
      todayTouches: screens.reduce((sum, s) => sum + s.today.touches, 0),
      todayPopupOpens: screens.reduce((sum, s) => sum + s.today.popupOpens, 0),
    }),
    [screens]
  );

  // "Bugün" İstanbul günüdür; tarayıcının saat diliminden bağımsız olarak API'den gelir.
  const today = allScreens.length
    ? dayjs(allScreens[0].today.date).format("DD.MM")
    : "Bugün";

  const handleExport = useCallback(() => {
    const rows = screens.map((s) => ({
      Durum: STATUS_LABELS[s.status],
      "Mağaza Kodu": s.shopCode || "",
      Mağaza: shopLabel(s),
      Şehir: s.shop?.city || "",
      "Mağaza eşleşti": s.shopMatched ? "Evet" : "Hayır",
      Ekran: s.screen,
      "Ekran ID": s.screenId ?? "",
      "Son sinyal": formatDateTime(s.lastSeenAt),
      "Son dokunma": formatDateTime(s.lastTouchAt),
      "Dokunmasız açık (dk)": Math.floor(s.openSecSinceLastTouch / 60),
      "Bugün açık (dk)": s.today.openMinutes,
      "Bugün dokunma": s.today.touches,
      "Bugün popup": s.today.popupOpens,
      "Bugün yeniden başlama": s.today.restarts,
      Video: formatVideo(s),
      "Son kesinti": s.lastGap
        ? `${GAP_KIND_LABELS[s.lastGap.kind]} ${formatDateTime(
            s.lastGap.from
          )} → ${formatDateTime(s.lastGap.to)}`
        : "",
      Build: s.build || "",
      "Cihaz UUID": s.deviceUuid,
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Prizma Ekranları");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/octet-stream" }),
      "prizma-ekranlari.xlsx"
    );
  }, [screens]);

  return (
    <>
      <Row gutter={[16, 16]}>
        <Col xs={24} sm={12} xl={4}>
          <KpiCard
            icon={<Icons.DesktopOutlined />}
            label="Ekran"
            value={summary.total}
          />
        </Col>
        <Col xs={24} sm={12} xl={4}>
          <KpiCard
            icon={<Icons.WifiOutlined />}
            label="Sinyal var"
            value={summary.online}
            color="#52c41a"
          />
        </Col>
        <Col xs={24} sm={12} xl={4}>
          <KpiCard
            icon={<Icons.DisconnectOutlined />}
            label="Sinyal yok"
            value={summary.noSignal}
            color="#ff4d4f"
          />
        </Col>
        <Col xs={24} sm={12} xl={4}>
          <KpiCard
            icon={<Icons.ShopOutlined />}
            label="Mağaza eşleşmedi"
            value={summary.shopUnmatched}
            color="#fa8c16"
          />
        </Col>
        <Col xs={24} sm={12} xl={4}>
          <KpiCard
            icon={<Icons.AimOutlined />}
            label={`Dokunma (${today})`}
            value={summary.todayTouches}
            color="#FFC900"
          />
        </Col>
        <Col xs={24} sm={12} xl={4}>
          <KpiCard
            icon={<Icons.ShoppingOutlined />}
            label={`Popup açılma (${today})`}
            value={summary.todayPopupOpens}
            color="#18619c"
          />
        </Col>
      </Row>

      <Typography.Paragraph type="secondary" style={{ marginTop: 12 }}>
        Sinyal yok: {OPEN_HOURS_LABEL} (TR) arasında 15 dakikadan uzun süredir
        sinyal gelmedi; bugün hiç gelmediyse "Bugün açılmadı" yazar. Bu saatler
        dışında sinyal gelmeyen ekran "Mesai dışı" görünür ve sorun sayılmaz;
        açık süreye de eklenmez. Liste dakikada bir yenilenir.
      </Typography.Paragraph>

      <Card
        title="Prizma Ekranları"
        extra={
          <Space wrap>
            <Input.Search
              placeholder="Mağaza, şehir, ekran ara"
              allowClear
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 240 }}
            />
            {dataUpdatedAt > 0 && (
              <Typography.Text type="secondary">
                Güncellendi {dayjs(dataUpdatedAt).format("HH:mm:ss")}
              </Typography.Text>
            )}
            <Button
              icon={<Icons.ReloadOutlined />}
              loading={isFetching}
              onClick={() => refetch()}
            >
              Yenile
            </Button>
            <Button
              icon={<Icons.DownloadOutlined />}
              onClick={handleExport}
              disabled={screens.length === 0}
            >
              İndir
            </Button>
          </Space>
        }
      >
        <PrizmaScreensTable screens={screens} loading={isLoading} />
      </Card>
    </>
  );
};
